import React, { useMemo, useState } from "react";
import { Plus, Trash2, ImagePlus, Loader2, Save, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const asset = (u) => (!u ? null : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const EMOTIONS = ["neutral", "happy", "sad", "angry", "surprised", "thinking"];
const clone = (o) => JSON.parse(JSON.stringify(o || {}));

function nodeType(n) {
  if (n.ending) return "ending";
  if ((n.choices || []).length) return "branch";
  return "beat";
}

export default function VNEditor({ scene, onSaved }) {
  const [nodes, setNodes] = useState(() => clone(scene.nodes));
  const [start, setStart] = useState(scene.start);
  const [backgrounds, setBackgrounds] = useState(() => clone(scene.backgrounds));
  const [bgUrls, setBgUrls] = useState(() => clone(scene.bg_urls));
  const [saving, setSaving] = useState(false);
  const [repainting, setRepainting] = useState("");

  const castNames = useMemo(() => ["Narrator", ...(scene.cast || []).map((c) => c.name)], [scene.cast]);
  const nodeIds = Object.keys(nodes);
  const bgKeys = Object.keys(backgrounds);

  const update = (id, fn) => setNodes((prev) => { const nx = clone(prev); fn(nx[id]); return nx; });

  const setType = (id, t) => update(id, (n) => {
    if (t === "beat") { n.ending = false; n.ending_title = ""; n.choices = []; n.next = nodeIds.find((x) => x !== id) || null; }
    if (t === "branch") { n.ending = false; n.ending_title = ""; n.next = null; if (!(n.choices || []).length) n.choices = [{ text: "Continue", goto: nodeIds.find((x) => x !== id) || id }]; }
    if (t === "ending") { n.ending = true; n.choices = []; n.next = null; n.ending_title = n.ending_title || "The End"; }
  });

  const addNode = () => {
    let i = 1; while (nodes[`n${i}`]) i += 1;
    const id = `n${i}`;
    setNodes((prev) => ({ ...clone(prev), [id]: { id, bg: bgKeys[0] || "", lines: [{ speaker: castNames[0], emotion: "neutral", text: "" }], choices: [], next: null, ending: true, ending_title: "New ending" } }));
  };
  const delNode = (id) => setNodes((prev) => { const nx = clone(prev); delete nx[id]; return nx; });

  const repaint = async (bgId) => {
    setRepainting(bgId);
    try {
      const { data } = await api.post(`/vn/scenes/${scene.id}/regen-background`, { bg_id: bgId, prompt: backgrounds[bgId] || "" });
      setBgUrls((u) => ({ ...u, [bgId]: data.url }));
      toast.success("Background repainted! 🎨");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't repaint."); }
    finally { setRepainting(""); }
  };

  const save = async () => {
    const bad = Object.entries(nodes).find(([, n]) => !(n.lines || []).some((l) => (l.text || "").trim()));
    if (bad) { toast.error(`Beat "${bad[0]}" needs at least one line with text.`); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/vn/scenes/${scene.id}/script`, { start, nodes, backgrounds });
      toast.success("Scene saved! ✨");
      onSaved?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the scene."); }
    finally { setSaving(false); }
  };

  const inputCls = "rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-2 py-1.5 text-sm";

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 space-y-5" data-testid="vn-editor">
      {/* Start + save */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="text-xs font-semibold flex items-center gap-2">Opening beat
          <select value={start} onChange={(e) => setStart(e.target.value)} data-testid="vn-editor-start" className={inputCls}>
            {nodeIds.map((id) => <option key={id} value={id}>{id}</option>)}
          </select>
        </label>
        <button onClick={save} disabled={saving} data-testid="vn-editor-save"
          className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save scene
        </button>
      </div>

      {/* Backgrounds */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Backgrounds</h4>
        <div className="space-y-2">
          {bgKeys.map((k) => (
            <div key={k} className="flex items-start gap-2" data-testid={`vn-editor-bg-${k}`}>
              <div className="w-20 h-12 rounded-lg overflow-hidden bg-[hsl(var(--secondary))] shrink-0 grid place-items-center">
                {bgUrls[k] ? <img src={asset(bgUrls[k])} alt={k} className="w-full h-full object-cover" /> : <ImagePlus className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />}
              </div>
              <textarea value={backgrounds[k]} onChange={(e) => setBackgrounds((b) => ({ ...b, [k]: e.target.value }))} rows={2}
                data-testid={`vn-editor-bg-prompt-${k}`} className={`${inputCls} flex-1 resize-none`} />
              <button onClick={() => repaint(k)} disabled={repainting === k} data-testid={`vn-editor-repaint-${k}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-3 py-2 text-xs font-semibold hover:bg-[hsl(var(--secondary))] disabled:opacity-50 shrink-0">
                {repainting === k ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Repaint
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Nodes */}
      <div className="space-y-3">
        <h4 className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Beats</h4>
        {nodeIds.map((id) => {
          const n = nodes[id];
          const t = nodeType(n);
          return (
            <div key={id} className="rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`vn-editor-node-${id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-xs font-bold rounded-md bg-[hsl(var(--secondary))] px-2 py-1">{id}{id === start ? " · start" : ""}</span>
                <select value={n.bg} onChange={(e) => update(id, (nd) => { nd.bg = e.target.value; })} className={`${inputCls} text-xs`} data-testid={`vn-editor-nodebg-${id}`}>
                  {bgKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <div className="inline-flex rounded-lg border border-[hsl(var(--border))] overflow-hidden text-xs">
                  {["beat", "branch", "ending"].map((tt) => (
                    <button key={tt} onClick={() => setType(id, tt)} data-testid={`vn-editor-type-${id}-${tt}`}
                      className={`px-2.5 py-1.5 capitalize ${t === tt ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-[hsl(var(--secondary))]"}`}>{tt}</button>
                  ))}
                </div>
                <button onClick={() => delNode(id)} data-testid={`vn-editor-delnode-${id}`} className="ml-auto w-7 h-7 grid place-items-center rounded-full text-rose-500 hover:bg-rose-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>

              {/* Lines */}
              <div className="space-y-2 mb-2">
                {(n.lines || []).map((ln, li) => (
                  <div key={li} className="flex flex-wrap items-start gap-2" data-testid={`vn-editor-line-${id}-${li}`}>
                    <select value={ln.speaker} onChange={(e) => update(id, (nd) => { nd.lines[li].speaker = e.target.value; })} className={`${inputCls} text-xs`}>
                      {castNames.map((nm) => <option key={nm} value={nm}>{nm}</option>)}
                    </select>
                    <select value={ln.emotion} onChange={(e) => update(id, (nd) => { nd.lines[li].emotion = e.target.value; })} className={`${inputCls} text-xs`}>
                      {EMOTIONS.map((em) => <option key={em} value={em}>{em}</option>)}
                    </select>
                    <input value={ln.text} onChange={(e) => update(id, (nd) => { nd.lines[li].text = e.target.value; })} placeholder="Line…" className={`${inputCls} flex-1 min-w-[160px]`} />
                    <button onClick={() => update(id, (nd) => { nd.lines.splice(li, 1); })} className="w-7 h-7 grid place-items-center rounded-full text-rose-500 hover:bg-rose-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <button onClick={() => update(id, (nd) => { (nd.lines = nd.lines || []).push({ speaker: castNames[0], emotion: "neutral", text: "" }); })}
                  data-testid={`vn-editor-addline-${id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--primary))]"><Plus className="w-3.5 h-3.5" /> Add line</button>
              </div>

              {/* Type-specific */}
              {t === "branch" && (
                <div className="space-y-2 border-t border-[hsl(var(--border))] pt-2">
                  {(n.choices || []).map((c, ci) => (
                    <div key={ci} className="flex items-center gap-2" data-testid={`vn-editor-choice-${id}-${ci}`}>
                      <input value={c.text} onChange={(e) => update(id, (nd) => { nd.choices[ci].text = e.target.value; })} placeholder="Choice…" className={`${inputCls} flex-1`} />
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">→</span>
                      <select value={c.goto} onChange={(e) => update(id, (nd) => { nd.choices[ci].goto = e.target.value; })} className={`${inputCls} text-xs`}>
                        {nodeIds.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                      <button onClick={() => update(id, (nd) => { nd.choices.splice(ci, 1); })} className="w-7 h-7 grid place-items-center rounded-full text-rose-500 hover:bg-rose-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <button onClick={() => update(id, (nd) => { (nd.choices = nd.choices || []).push({ text: "New choice", goto: nodeIds.find((x) => x !== id) || id }); })}
                    data-testid={`vn-editor-addchoice-${id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--primary))]"><Plus className="w-3.5 h-3.5" /> Add choice</button>
                </div>
              )}
              {t === "beat" && (
                <div className="border-t border-[hsl(var(--border))] pt-2 text-xs flex items-center gap-2">
                  <span className="text-[hsl(var(--muted-foreground))]">Then go to</span>
                  <select value={n.next || ""} onChange={(e) => update(id, (nd) => { nd.next = e.target.value; })} className={`${inputCls} text-xs`} data-testid={`vn-editor-next-${id}`}>
                    {nodeIds.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
              )}
              {t === "ending" && (
                <div className="border-t border-[hsl(var(--border))] pt-2">
                  <input value={n.ending_title || ""} onChange={(e) => update(id, (nd) => { nd.ending_title = e.target.value; })} placeholder="Ending title…" data-testid={`vn-editor-ending-${id}`} className={`${inputCls} w-full`} />
                </div>
              )}
            </div>
          );
        })}
        <button onClick={addNode} data-testid="vn-editor-addnode" className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[hsl(var(--border))] px-4 py-2 text-sm font-semibold hover:bg-[hsl(var(--secondary))]"><Plus className="w-4 h-4" /> Add beat</button>
      </div>
    </div>
  );
}
